import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Paper,
  Fade,
  Avatar,
  Chip
} from '@mui/material';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import CasinoIcon from '@mui/icons-material/Casino';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import LocalDrinkIcon from '@mui/icons-material/LocalDrink';
import SportsBarIcon from '@mui/icons-material/SportsBar';
import BaseRound from './BaseRound';
import { useGame } from '../../contexts/GameContext';

const challenges = [
  {
    name: "Armdrücken",
    icon: FitnessCenterIcon,
    description: "Fordere deinen Gegner zum Armdrücken heraus! ",
    drinks: 3
  },
  {
    name: "Schere-Stein-Papier",
    icon: ContentCutIcon,
    description: "Best of 3 - Möge der Bessere gewinnen!",
    drinks: 2
  },
  {
    name: "Daumenringen",
    icon: SportsKabaddiIcon,
    description: "Wer zuerst den Daumen des anderen für 3 Sekunden fixiert, gewinnt!",
    drinks: 2
  },
  {
    name: "Münzwurf-Challenge",
    icon: CasinoIcon,
    description: "Best of 3 - Wer errät öfter richtig?",
    drinks: 2
  },
  {
    name: "Getränk exen",
    icon: LocalDrinkIcon,
    description: "Wer zuerst sein Getränk exen kann, gewinnt!",
    drinks: 2
  },
  {
    name: "Leberschuss 2v2",
    icon: SportsBarIcon,
    description: "Wer die Runde verliert, trinkt einen Shot!",
    drinks: 1
  },
  {
    name: "Flaschendrehen",
    icon: AutorenewIcon,
    description: "Die Flasche bestimmt, wer trinkt!",
    drinks: 2
  }
];

export default function DuelRound() {
  const { state } = useGame();
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [winner, setWinner] = useState(null);

  // Wähle einen zufälligen Gegner aus (nicht der aktive Spieler)
  const selectRandomOpponent = () => {
    const availablePlayers = state.players.filter(p => p.name !== state.activePlayer);
    const randomOpponent = availablePlayers[Math.floor(Math.random() * availablePlayers.length)];
    setOpponent(randomOpponent.name);
  };

  const handleChallengeSelect = (challenge) => {
    setSelectedChallenge(challenge);
    selectRandomOpponent();
  };

  const handleWinnerSelect = (winnerName) => {
    setWinner(winnerName);
    setShowResult(true);
  };

  const resultData = showResult ? {
    title: `${selectedChallenge.name} - Duell beendet!`,
    success: winner === state.activePlayer,
    drinks: selectedChallenge.drinks,
    player: winner === state.activePlayer ? opponent : state.activePlayer,
    points: winner === state.activePlayer ? 1 : 0,
    customText: winner === state.activePlayer 
      ? `${winner} gewinnt und darf ${selectedChallenge.drinks} Schlücke verteilen! 🏆`
      : `${winner} gewinnt! ${state.activePlayer} muss ${selectedChallenge.drinks} Schlücke trinken! 🏆`
  } : null;

  if (!selectedChallenge) {
    // Wähle zwei zufällige Herausforderungen aus
    const randomChallenges = challenges.sort(() => 0.5 - Math.random()).slice(0, 2);
    return (
      <BaseRound
        instructions={`${state.activePlayer}, wähle eine Herausforderung!`}
        showResult={false}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {randomChallenges.map((challenge) => {
            const Icon = challenge.icon;
            return (
              <Button
                key={challenge.name}
                variant="outlined"
                onClick={() => handleChallengeSelect(challenge)}
                sx={{
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <Icon sx={{ fontSize: 40 }} />
                <Typography variant="h6" align="center">
                  {challenge.name}
                </Typography>
              </Button>
            );
          })}
        </Box>
      </BaseRound>
    );
  }

  if (!showResult) {
    return (
      <BaseRound
        instructions={`${state.activePlayer} vs ${opponent}`}
        showResult={false}
      >
        <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 3 
          }}>
            <Typography variant="h5" align="center" gutterBottom>
              {selectedChallenge.name}
            </Typography>

            <Typography variant="body1" align="center" color="text.secondary">
              {selectedChallenge.description}
            </Typography>

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              mt: 2
            }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                {state.activePlayer[0]}
              </Avatar>
              <Typography variant="h6" color="primary">
                VS
              </Typography>
              <Avatar sx={{ bgcolor: 'secondary.main' }}>
                {opponent[0]}
              </Avatar>
            </Box>

            <Typography variant="h6" align="center" sx={{ mt: 2 }}>
              Wer hat gewonnen?
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleWinnerSelect(state.activePlayer)}
                sx={{ minWidth: 120 }}
              >
                {state.activePlayer}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleWinnerSelect(opponent)}
                sx={{ minWidth: 120 }}
              >
                {opponent}
              </Button>
            </Box>
          </Box>
        </Paper>
      </BaseRound>
    );
  }

  return (
    <BaseRound
      showResult={showResult}
      resultData={resultData}
    />
  );
} 